import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Users,
  UserPlus,
  Search,
  Phone,
  Mail,
  Building2,
  Edit2,
  Trash2,
  X,
  Check,
  Loader2,
  Eye,
  EyeOff,
  Smartphone,
  MapPin,
  Calendar,
  Filter,
  Download,
  Upload,
  FolderOpen,
} from "lucide-react";

import StaffDocumentsModal from "../components/StaffDocumentsModal";


const DEPARTMENTS = [
  "Management",
  "Sales",
  "Operations",
  "Customer Service",
  "Accounting",
  "IT",
  "Logistics",
  "Documentation",
  "Customs",
  "Warehouse",
];

const ROLES = [
  "Director",
  "Manager",
  "Senior Specialist",
  "Specialist",
  "Coordinator",
  "Assistant",
  "Trainee",
];

const VERTICALS = [
  { value: "Europacific Logistika d.o.o.", label: "Europacific Logistika" },
  { value: "EP Holding d.o.o.", label: "EP Holding" },
  { value: "Cargo CFS d.o.o.", label: "Cargo CFS" },
  { value: "Artcom EPT d.o.o.", label: "Artcom EPT" },
  { value: "AM-Tech 2.0 d.o.o.", label: "AM-Tech 2.0" },
];

export default function StaffManagementPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [docsStaff, setDocsStaff] = useState(null);  // staff member whose HR docs are open
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    office_phone: "",
    mobile_phone: "",
    department: "",
    role: "",
    office_location: "",
    show_in_directory: true,
    notes: "",
    company: "",
    job_title: "",
    birthday: "",
    start_date: "",
  });

  useEffect(() => {
    fetchStaff();
  }, [token]);

  useEffect(() => {
    filterStaff();
  }, [staff, searchQuery, selectedDepartment]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Failed to load staff directory");
    } finally {
      setLoading(false);
    }
  };

  const filterStaff = () => {
    let filtered = [...staff];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.first_name?.toLowerCase().includes(query) ||
          s.last_name?.toLowerCase().includes(query) ||
          s.email?.toLowerCase().includes(query) ||
          s.department?.toLowerCase().includes(query) ||
          s.role?.toLowerCase().includes(query)
      );
    }
    
    if (selectedDepartment !== "all") {
      filtered = filtered.filter((s) => s.department === selectedDepartment);
    }
    
    setFilteredStaff(filtered);
  };

  const openAddModal = () => {
    setEditingStaff(null);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      office_phone: "",
      mobile_phone: "",
      department: "",
      role: "",
      office_location: "",
      show_in_directory: true,
      notes: "",
      company: "",
      job_title: "",
      birthday: "",
      start_date: "",
    });
    setShowModal(true);
  };

  const openEditModal = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      first_name: staffMember.first_name || "",
      last_name: staffMember.last_name || "",
      email: staffMember.email || "",
      office_phone: staffMember.office_phone || "",
      mobile_phone: staffMember.mobile_phone || staffMember.mobile || "",
      department: staffMember.department || "",
      role: staffMember.role || "",
      office_location: staffMember.office_location || "",
      show_in_directory: staffMember.show_in_directory ?? true,
      notes: staffMember.notes || "",
      company: staffMember.company || "",
      job_title: staffMember.job_title || "",
      birthday: staffMember.birthday || "",
      start_date: staffMember.start_date || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error("First name and last name are required");
      return;
    }

    setSaving(true);
    try {
      if (editingStaff) {
        // Update existing
        await axios.put(
          `${API_URL}/staff/${editingStaff.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Staff member updated");
      } else {
        // Create new
        await axios.post(
          `${API_URL}/staff`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Staff member added");
      }
      setShowModal(false);
      fetchStaff();
    } catch (error) {
      console.error("Error saving staff:", error);
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (staffMember) => {
    if (!window.confirm(`Delete ${staffMember.first_name} ${staffMember.last_name}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/staff/${staffMember.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Staff member deleted");
      fetchStaff();
    } catch (error) {
      toast.error("Failed to delete staff member");
    }
  };

  const toggleDirectoryVisibility = async (staffMember) => {
    try {
      await axios.patch(
        `${API_URL}/staff/${staffMember.id}/toggle-directory`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchStaff();
      toast.success(
        staffMember.show_in_directory
          ? "Hidden from customer directory"
          : "Now visible in customer directory"
      );
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  // Group staff by department for display
  const groupedByDepartment = filteredStaff.reduce((acc, s) => {
    const dept = s.department || "Other";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(s);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              Staff Directory
            </h1>
            <p className="text-slate-600 mt-1">
              Manage employee information and customer-facing directory
            </p>
          </div>
          <Button onClick={openAddModal} data-testid="add-staff-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Staff Member
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Staff</p>
                <p className="text-xl font-bold">{staff.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">In Directory</p>
                <p className="text-xl font-bold">
                  {staff.filter((s) => s.show_in_directory).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Departments</p>
                <p className="text-xl font-bold">
                  {new Set(staff.map((s) => s.department).filter(Boolean)).size}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <MapPin className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Locations</p>
                <p className="text-xl font-bold">
                  {new Set(staff.map((s) => s.office_location).filter(Boolean)).size || 1}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="h-10 px-3 rounded-md border border-slate-200 text-sm"
              >
                <option value="all">All Departments</option>
                {DEPARTMENTS.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Staff List by Department */}
        {Object.keys(groupedByDepartment).length === 0 ? (
          <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
            <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Staff Members</h3>
            <p className="text-slate-500 mb-6">
              Add your first staff member to get started
            </p>
            <Button onClick={openAddModal}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Staff Member
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByDepartment).map(([department, members]) => (
              <motion.div
                key={department}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border shadow-sm overflow-hidden"
              >
                <div className="bg-slate-50 px-4 py-3 border-b flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  <h2 className="font-semibold text-slate-700">{department}</h2>
                  <span className="text-sm text-slate-500">({members.length})</span>
                </div>
                <div className="divide-y">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="p-4 hover:bg-slate-50 transition-colors flex items-center gap-4"
                    >
                      {/* Avatar */}
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">
                            {member.first_name} {member.last_name}
                          </h3>
                          {member.show_in_directory ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                              In Directory
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">
                              Hidden
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{member.role}</p>
                      </div>

                      {/* Contact Info */}
                      <div className="hidden md:flex items-center gap-6 text-sm">
                        {member.email && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Mail className="h-4 w-4" />
                            <span>{member.email}</span>
                          </div>
                        )}
                        {member.office_phone && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Phone className="h-4 w-4" />
                            <span>{member.office_phone}</span>
                          </div>
                        )}
                        {member.mobile_phone && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Smartphone className="h-4 w-4" />
                            <span>{member.mobile_phone}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDocsStaff(member)}
                          title="HR Documents"
                          data-testid={`staff-docs-btn-${member.id}`}
                        >
                          <FolderOpen className="h-4 w-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleDirectoryVisibility(member)}
                          title={member.show_in_directory ? "Hide from directory" : "Show in directory"}
                        >
                          {member.show_in_directory ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(member)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(member)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {docsStaff && (
            <StaffDocumentsModal
              staff={docsStaff}
              token={token}
              isAdmin={!!user?.is_admin}
              onClose={() => setDocsStaff(null)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b flex items-center justify-between">
                  <h2 className="text-xl font-semibold">
                    {editingStaff ? "Edit Staff Member" : "Add Staff Member"}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) =>
                          setFormData({ ...formData, first_name: e.target.value })
                        }
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) =>
                          setFormData({ ...formData, last_name: e.target.value })
                        }
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="john.doe@europacific.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Office Phone</Label>
                      <Input
                        value={formData.office_phone}
                        onChange={(e) =>
                          setFormData({ ...formData, office_phone: e.target.value })
                        }
                        placeholder="+386 5 663 2200"
                      />
                    </div>
                    <div>
                      <Label>Mobile Phone</Label>
                      <Input
                        value={formData.mobile_phone}
                        onChange={(e) =>
                          setFormData({ ...formData, mobile_phone: e.target.value })
                        }
                        placeholder="+386 40 123 456"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Department</Label>
                      <select
                        value={formData.department}
                        onChange={(e) =>
                          setFormData({ ...formData, department: e.target.value })
                        }
                        className="w-full h-10 px-3 rounded-md border border-slate-200"
                      >
                        <option value="">Select department</option>
                        {DEPARTMENTS.map((dept) => (
                          <option key={dept} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Role</Label>
                      <select
                        value={formData.role}
                        onChange={(e) =>
                          setFormData({ ...formData, role: e.target.value })
                        }
                        className="w-full h-10 px-3 rounded-md border border-slate-200"
                      >
                        <option value="">Select role</option>
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Office Location</Label>
                    <Input
                      value={formData.office_location}
                      onChange={(e) =>
                        setFormData({ ...formData, office_location: e.target.value })
                      }
                      placeholder="Koper, Slovenia"
                    />
                  </div>

                  {/* Vertical / Company */}
                  <div>
                    <Label>Vertical</Label>
                    <select
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      className="w-full h-10 px-3 rounded-md border border-slate-200"
                    >
                      <option value="">Select vertical</option>
                      {VERTICALS.map((v) => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Job Title */}
                  <div>
                    <Label>Job Title / Position</Label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) =>
                        setFormData({ ...formData, job_title: e.target.value })
                      }
                      placeholder="e.g., Carinski deklerant, Vodja pisarne"
                    />
                  </div>

                  {/* Birthday & Onboard Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Birthday</Label>
                      <Input
                        type="date"
                        value={formData.birthday}
                        onChange={(e) =>
                          setFormData({ ...formData, birthday: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label>Onboard Date (VSTOP)</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) =>
                          setFormData({ ...formData, start_date: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="show_in_directory"
                      checked={formData.show_in_directory}
                      onChange={(e) =>
                        setFormData({ ...formData, show_in_directory: e.target.checked })
                      }
                      className="h-4 w-4"
                    />
                    <label htmlFor="show_in_directory" className="text-sm">
                      <span className="font-medium">Show in Customer Directory</span>
                      <p className="text-slate-500 text-xs">
                        Contact info will be visible to customers in the app
                      </p>
                    </label>
                  </div>

                  <div>
                    <Label>Internal Notes</Label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      placeholder="Any internal notes about this staff member..."
                      className="w-full p-3 rounded-md border border-slate-200 text-sm resize-none h-20"
                    />
                  </div>
                </div>

                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    {editingStaff ? "Save Changes" : "Add Staff Member"}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
